/**
 * Shop pricing rules. Client-safe pure module — no server imports.
 * Money is ALWAYS integer cents; format only at the edge with formatEur.
 */

export const SHIPPING_CENTS = 500 // flat PostNL rate
export const POSTCARD_UNIT_CENTS = 150
export const POSTCARD_BUNDLE_SIZE = 5
export const POSTCARD_BUNDLE_CENTS = 500

/**
 * Postcard bundle: EUR 1.50 per card, any 5 cards for EUR 5. The bundle
 * applies across the whole postcard quantity: floor(n/5) bundles plus the
 * remainder at unit price.
 */
export function postcardTotalCents(quantity: number) {
	const qty = Math.max(0, Math.floor(quantity))
	return (
		Math.floor(qty / POSTCARD_BUNDLE_SIZE) * POSTCARD_BUNDLE_CENTS +
		(qty % POSTCARD_BUNDLE_SIZE) * POSTCARD_UNIT_CENTS
	)
}

/** What the bundle saves versus n × unit price (0 when no full bundle). */
export function postcardBundleDiscountCents(quantity: number) {
	const qty = Math.max(0, Math.floor(quantity))
	return qty * POSTCARD_UNIT_CENTS - postcardTotalCents(qty)
}

export interface CartPricingLine {
	type: string
	unitPriceCents: number
	quantity: number
}

/**
 * Totals for a cart. Postcard lines are pooled across products: the bundle
 * prices the combined postcard quantity, regardless of which cards are in
 * the mix. Non-postcard lines are unitPrice × qty. Flat shipping applies
 * whenever the cart holds at least one item.
 */
export function cartTotals(lines: Array<CartPricingLine>) {
	let postcardQty = 0
	let otherCents = 0
	let itemCount = 0
	for (const line of lines) {
		const qty = Math.max(0, Math.floor(line.quantity))
		itemCount += qty
		if (line.type === 'postcard') {
			postcardQty += qty
		} else {
			otherCents += line.unitPriceCents * qty
		}
	}
	const subtotalCents = otherCents + postcardTotalCents(postcardQty)
	const shippingCents = itemCount > 0 ? SHIPPING_CENTS : 0
	return {
		subtotalCents,
		shippingCents,
		totalCents: subtotalCents + shippingCents,
	}
}

export interface VatLine {
	/** VAT-inclusive amount; negative for discount lines. */
	amountCents: number
	vatRatePercent: number
}

/**
 * Per-rate VAT buckets for VAT-inclusive line amounts (invoice breakdown).
 * Lines sharing a rate are pooled first, then the VAT inside the pooled
 * amount is `round(amount × rate / (100 + rate))` — so discount lines simply
 * shrink their rate's bucket. Buckets come back sorted by rate ascending.
 */
export function vatBreakdown(lines: Array<VatLine>) {
	const byRate = new Map<number, number>()
	for (const line of lines) {
		byRate.set(
			line.vatRatePercent,
			(byRate.get(line.vatRatePercent) ?? 0) + line.amountCents,
		)
	}
	return [...byRate.entries()]
		.sort(([a], [b]) => a - b)
		.map(([ratePercent, amountCents]) => {
			const vatCents = Math.round(
				(amountCents * ratePercent) / (100 + ratePercent),
			)
			return { ratePercent, baseCents: amountCents - vatCents, vatCents }
		})
}

const eurFormatter = new Intl.NumberFormat('nl-NL', {
	style: 'currency',
	currency: 'EUR',
})

export function formatEur(cents: number) {
	return eurFormatter.format(cents / 100)
}
